import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Line = { id: number; code: string; name: string };
type Rep = {
  id: number;
  username: string;
  fullName: string;
  email: string | null;
  role: 'REP' | 'ADMIN';
  isActive: boolean;
  lastLoginAt: string | null;
  lines: Line[];
};

export default function Reps() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ username: '', fullName: '', password: '', lineIds: [] as number[] });
  const [resetting, setResetting] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const reps = useQuery<{ reps: Rep[] }>({
    queryKey: ['admin', 'reps'],
    queryFn: () => api('/api/admin/reps'),
  });
  const lines = useQuery<{ lines: Line[] }>({
    queryKey: ['admin', 'lines'],
    queryFn: () => api('/api/admin/lines'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'reps'] });

  const create = useMutation({
    mutationFn: () => api('/api/admin/reps', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => {
      setForm({ username: '', fullName: '', password: '', lineIds: [] });
      void invalidate();
    },
  });

  const setLines = useMutation({
    mutationFn: ({ id, lineIds }: { id: number; lineIds: number[] }) =>
      api(`/api/admin/reps/${id}`, { method: 'PATCH', body: JSON.stringify({ lineIds }) }),
    onSuccess: invalidate,
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api(`/api/admin/reps/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      api(`/api/admin/reps/${id}/password`, { method: 'POST', body: JSON.stringify({ password }) }),
    onSuccess: () => {
      setResetting(null);
      setNewPassword('');
    },
  });

  const allLines = lines.data?.lines ?? [];

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Nouveau représentant</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          className="space-y-4"
        >
          <div className="flex flex-wrap gap-3">
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Nom d'utilisateur</span>
              <input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                className="border-input bg-background h-11 w-48 rounded-lg border px-3 font-mono"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Nom complet</span>
              <input
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className="border-input bg-background h-11 w-64 rounded-lg border px-3"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Mot de passe (8 min.)</span>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="border-input bg-background h-11 w-48 rounded-lg border px-3 font-mono"
                required
              />
            </label>
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-medium">Codes de ligne</legend>
            <div className="flex flex-wrap gap-2">
              {allLines.map((l) => {
                const checked = form.lineIds.includes(l.id);
                return (
                  <label
                    key={l.id}
                    className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${
                      checked ? 'border-primary bg-primary/10 font-medium' : 'border-input'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() =>
                        setForm({
                          ...form,
                          lineIds: checked
                            ? form.lineIds.filter((id) => id !== l.id)
                            : [...form.lineIds, l.id],
                        })
                      }
                    />
                    {l.code} — {l.name}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <button
            type="submit"
            disabled={create.isPending}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-50"
          >
            Créer
          </button>
          {create.isError && (
            <p className="text-destructive text-sm font-medium">{(create.error as Error).message}</p>
          )}
        </form>
      </section>

      <section className="bg-card space-y-4 rounded-xl border p-6">
        <h2 className="text-lg font-semibold">Représentants</h2>

        {reps.data?.reps.map((r) => (
          <div key={r.id} className="space-y-3 rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">
                  {r.fullName}{' '}
                  <span className="text-muted-foreground font-mono text-sm">({r.username})</span>
                  {r.role === 'ADMIN' && (
                    <span className="bg-secondary ml-2 rounded px-2 py-0.5 text-xs font-medium">admin</span>
                  )}
                  {!r.isActive && <span className="text-destructive ml-2 text-xs">désactivé</span>}
                </p>
                <p className="text-muted-foreground text-xs">
                  Dernière connexion :{' '}
                  {r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString('fr-CA') : 'jamais'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setResetting(resetting === r.id ? null : r.id)}
                  className="border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  Mot de passe
                </button>
                <button
                  onClick={() => toggle.mutate({ id: r.id, isActive: !r.isActive })}
                  className="border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  {r.isActive ? 'Désactiver' : 'Réactiver'}
                </button>
              </div>
            </div>

            {resetting === r.id && (
              <div className="flex flex-wrap items-end gap-2">
                <input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe"
                  className="border-input bg-background h-10 w-56 rounded-lg border px-3 font-mono text-sm"
                />
                <button
                  onClick={() => resetPassword.mutate({ id: r.id, password: newPassword })}
                  disabled={newPassword.length < 8 || resetPassword.isPending}
                  className="bg-primary text-primary-foreground h-10 rounded-lg px-4 text-sm font-medium disabled:opacity-40"
                >
                  Appliquer
                </button>
                {resetPassword.isError && (
                  <p className="text-destructive text-sm">{(resetPassword.error as Error).message}</p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {allLines.map((l) => {
                const has = r.lines.some((x) => x.id === l.id);
                return (
                  <button
                    key={l.id}
                    onClick={() =>
                      setLines.mutate({
                        id: r.id,
                        lineIds: has
                          ? r.lines.filter((x) => x.id !== l.id).map((x) => x.id)
                          : [...r.lines.map((x) => x.id), l.id],
                      })
                    }
                    className={`rounded-lg border px-3 py-1.5 text-sm ${
                      has ? 'border-primary bg-primary/10 font-medium' : 'border-input text-muted-foreground'
                    }`}
                  >
                    {l.code}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <p className="text-muted-foreground text-xs">
          Retirer un code de ligne coupe l'accès aux produits correspondants immédiatement, sans
          attendre la reconnexion du rep.
        </p>
      </section>
    </div>
  );
}
